<template>
  <div style="position: relative">
    <div
      class="rounded-lg"
      :class="$vuetify.display.xs ? 'pa-3' : 'pa-5'"
      style="background-color: #ffffff; border: 1px solid #bfbfbf"
    >
      <v-col cols="12" sm="12" lg="12" xl="12" class="pa-0">
        <!-- Desktop header layout -->
        <div class="d-none d-sm-flex align-center" style="gap: 12px; color: #191919">
          <UiChip size="small" variant="default">{{ course.coursePrefixDisplayLabel || course.coursePrefix }}</UiChip>
          <strong
            class="text-truncate font-weight-bold"
            style="flex: 1; min-width: 0; font-size: 16px; color: #191919"
            >{{ course.courseName }}</strong
          >
          <span
            v-if="course.badge?.show"
            class="ms-2 flex-shrink-0 font-weight-bold"
            style="
              padding-bottom: 4px;
              color: #191919;
              font-size: 14px;
              line-height: 23px;
              white-space: nowrap;
              text-align: right;
            "
          >
            <v-icon size="14" style="color: #191919; padding-right: 6px">{{ course.badge.icon }}</v-icon
            >{{ course.badge.text }}
          </span>
          <div
            v-if="course.ui?.showEnrollmentFee"
            class="flex-shrink-0 px-2 py-0 font-weight-bold rounded"
            :style="{
              marginLeft: course.flags?.isPathwayCourse && showPathwayBadge ? '12px' : 'auto',
              backgroundColor: 'var(--asu-warning-yellow-light)'
            }"
          >
            ${{ course.payment?.creditEligibilityPrice || 25 }} Enrollment fee
          </div>
        </div>

        <!-- Mobile header layout -->
        <div class="d-flex d-sm-none flex-column align-start" style="gap: 2px; color: #191919">
          <div class="d-flex align-center w-100 mb-1">
            <UiChip size="small" variant="default">{{ course.coursePrefixDisplayLabel || course.coursePrefix }}</UiChip>
            <div
              v-if="course.ui?.showEnrollmentFee"
              class="ml-auto flex-shrink-0 px-2 py-0 font-weight-bold rounded"
              style="background-color: var(--asu-warning-yellow-light); font-size: 12px"
            >
              ${{ course.payment?.creditEligibilityPrice || 25 }} fee
            </div>
          </div>
          <strong class="font-weight-bold" style="font-size: 15px; line-height: 1.3; color: #191919">{{
            course.courseName
          }}</strong>
          <span
            v-if="course.badge?.show"
            class="font-weight-bold mt-1"
            style="text-align: left; padding-left: 0; font-size: 11px; line-height: 1.3; color: #191919"
          >
            <v-icon size="12" style="color: #191919; padding-right: 4px">{{ course.badge.icon }}</v-icon
            >{{ course.badge.text }}
          </span>
        </div>

        <v-divider :class="$vuetify.display.xs ? 'my-2' : 'my-3'" style="border-color: #e0e0e0; opacity: 1" />

        <!-- Course details -->
        <v-row no-gutters justify-space-between wrap>
          <div class="w-100">
            <div class="text-center pa-0" style="border: none; background-color: transparent">
              <v-row no-gutters class="ma-0 pa-0">
                <!-- Left column: Course details (layout defined by backend) -->
                <v-col
                  :cols="isSmallScreen && !$vuetify.display.xs ? '8' : '12'"
                  sm="6"
                  :md="isSmallScreen ? '8' : '12'"
                  lg="6"
                  xl="6"
                  :class="[$vuetify.display.xs ? 'mb-2' : '', !isSmallScreen ? 'mb-3' : '']"
                >
                  <v-row no-gutters wrap>
                    <v-col cols="12" sm="12" md="12">
                      <!-- Layout: START, END, CREDITS (default for schedule/past) -->
                      <v-row
                        v-if="!detailsLayout || detailsLayout === 'start-end-credits'"
                        no-gutters
                        justify-start
                        wrap
                      >
                        <v-col
                          :cols="isSmallScreen ? '3' : '4'"
                          :sm="isSmallScreen ? '3' : '4'"
                          :md="isSmallScreen ? '3' : '4'"
                          :lg="isSmallScreen ? '3' : '4'"
                          class="text-left"
                        >
                          <div
                            class="text-uppercase font-weight-bold mb-1"
                            style="color: #777777; letter-spacing: 0.1em; font-size: 10px; letter-spacing: 0.91px"
                          >
                            START
                          </div>
                          <div class="d-flex align-center" style="gap: 6px">
                            <span
                              class="font-weight-bold"
                              :style="
                                $vuetify.display.xs
                                  ? 'font-size: 13px; letter-spacing: 0.5px;'
                                  : 'font-size: 14px; letter-spacing: 1px;'
                              "
                              >{{ course.offering?.formattedStartDate }}</span
                            >
                            <PartnershipWarning
                              v-model="partnershipWarningExpanded"
                              :warning="warningProp"
                              :show-banner="false"
                            />
                          </div>
                        </v-col>
                        <v-col
                          :cols="isSmallScreen ? '3' : '4'"
                          :sm="isSmallScreen ? '3' : '4'"
                          :md="isSmallScreen ? '3' : '4'"
                          :lg="isSmallScreen ? '3' : '4'"
                          class="text-left"
                        >
                          <div
                            class="text-uppercase font-weight-bold mb-1"
                            style="color: #777777; letter-spacing: 0.1em; font-size: 10px; letter-spacing: 0.91px"
                          >
                            END
                          </div>
                          <span
                            class="font-weight-bold"
                            :style="
                              $vuetify.display.xs
                                ? 'font-size: 13px; letter-spacing: 0.5px;'
                                : 'font-size: 14px; letter-spacing: 1px;'
                            "
                            >{{ course.offering?.formattedEndDate }}</span
                          >
                        </v-col>
                        <v-col
                          :cols="isSmallScreen ? '3' : '4'"
                          :sm="isSmallScreen ? '3' : '4'"
                          :md="isSmallScreen ? '3' : '4'"
                          :lg="isSmallScreen ? '3' : '4'"
                          class="text-left"
                        >
                          <div
                            class="text-uppercase font-weight-bold mb-1"
                            style="color: #777777; letter-spacing: 0.1em; font-size: 10px; letter-spacing: 0.91px"
                          >
                            CREDITS
                          </div>
                          <span
                            class="font-weight-bold"
                            :style="
                              $vuetify.display.xs
                                ? 'font-size: 13px; letter-spacing: 0.5px;'
                                : 'font-size: 14px; letter-spacing: 1px;'
                            "
                            >{{ course.credits }}</span
                          >
                        </v-col>
                      </v-row>

                      <!-- Layout: TRANSCRIPT BY, CREDITS, GRADE (for Add to Transcript) -->
                      <v-row v-else-if="detailsLayout === 'transcript-credits-grade'" no-gutters justify-start wrap>
                        <!-- ULC-3378: no deadline under an approved credit window override -->
                        <v-col
                          v-if="course.section?.formattedTranscriptDeadline"
                          cols="4"
                          sm="4"
                          md="4"
                          lg="4"
                          class="text-left"
                        >
                          <div
                            class="text-uppercase font-weight-bold mb-1"
                            style="color: #777777; letter-spacing: 0.1em; font-size: 10px; letter-spacing: 0.91px"
                          >
                            TRANSCRIPT BY
                          </div>
                          <span
                            class="font-weight-bold"
                            :style="
                              $vuetify.display.xs
                                ? 'font-size: 13px; letter-spacing: 0.5px;'
                                : 'font-size: 14px; letter-spacing: 1px;'
                            "
                            >{{ course.section?.formattedTranscriptDeadline }}</span
                          >
                        </v-col>
                        <v-col cols="4" sm="4" md="4" lg="4" class="text-left">
                          <div
                            class="text-uppercase font-weight-bold mb-1"
                            style="color: #777777; letter-spacing: 0.1em; font-size: 10px; letter-spacing: 0.91px"
                          >
                            CREDITS
                          </div>
                          <span
                            class="font-weight-bold"
                            :style="
                              $vuetify.display.xs
                                ? 'font-size: 13px; letter-spacing: 0.5px;'
                                : 'font-size: 14px; letter-spacing: 1px;'
                            "
                            >{{ course.credits }} Credits</span
                          >
                        </v-col>
                        <v-col cols="4" sm="4" md="4" lg="4" class="text-left">
                          <div
                            class="text-uppercase font-weight-bold mb-1"
                            style="color: #777777; letter-spacing: 0.1em; font-size: 10px; letter-spacing: 0.91px"
                          >
                            GRADE
                          </div>
                          <UiChip variant="secondary" size="medium">{{
                            course.section?.grade?.formatted || '—'
                          }}</UiChip>
                        </v-col>
                      </v-row>

                      <!-- Layout: START, END, GRADE (for View Transcript/Transcripted Courses) -->
                      <v-row v-else-if="detailsLayout === 'start-end-grade'" no-gutters justify-start wrap>
                        <v-col cols="4" sm="4" md="4" lg="4" class="text-left">
                          <div
                            class="text-uppercase font-weight-bold mb-1"
                            style="color: #777777; letter-spacing: 0.1em; font-size: 10px; letter-spacing: 0.91px"
                          >
                            START
                          </div>
                          <div class="d-flex align-center" style="gap: 6px">
                            <span
                              class="font-weight-bold"
                              :style="
                                $vuetify.display.xs
                                  ? 'font-size: 13px; letter-spacing: 0.5px;'
                                  : 'font-size: 14px; letter-spacing: 1px;'
                              "
                              >{{ course.offering?.formattedStartDate }}</span
                            >
                            <PartnershipWarning
                              v-model="partnershipWarningExpanded"
                              :warning="warningProp"
                              :show-banner="false"
                            />
                          </div>
                        </v-col>
                        <v-col cols="4" sm="4" md="4" lg="4" class="text-left">
                          <div
                            class="text-uppercase font-weight-bold mb-1"
                            style="color: #777777; letter-spacing: 0.1em; font-size: 10px; letter-spacing: 0.91px"
                          >
                            END
                          </div>
                          <span
                            class="font-weight-bold"
                            :style="
                              $vuetify.display.xs
                                ? 'font-size: 13px; letter-spacing: 0.5px;'
                                : 'font-size: 14px; letter-spacing: 1px;'
                            "
                            >{{ course.offering?.formattedEndDate }}</span
                          >
                        </v-col>
                        <v-col cols="4" sm="4" md="4" lg="4" class="text-left">
                          <div
                            class="text-uppercase font-weight-bold mb-1"
                            style="color: #777777; letter-spacing: 0.1em; font-size: 10px; letter-spacing: 0.91px"
                          >
                            GRADE
                          </div>
                          <UiChip variant="secondary" size="medium">{{
                            course.section?.grade?.formatted || '—'
                          }}</UiChip>
                        </v-col>
                      </v-row>

                      <!-- Layout: CREDITS, AVAILABLE -->
                      <v-row v-else-if="detailsLayout === 'credits-available'" no-gutters justify-start wrap>
                        <v-col cols="4" sm="4" md="4" lg="4" class="text-left">
                          <div
                            class="text-uppercase font-weight-bold mb-1"
                            style="color: #777777; letter-spacing: 0.1em; font-size: 10px; letter-spacing: 0.91px"
                          >
                            CREDITS
                          </div>
                          <span
                            class="font-weight-bold"
                            :style="
                              $vuetify.display.xs
                                ? 'font-size: 13px; letter-spacing: 0.5px;'
                                : 'font-size: 14px; letter-spacing: 1px;'
                            "
                            >{{ course.credits }} Credits</span
                          >
                        </v-col>
                        <v-col cols="8" sm="8" md="8" lg="8" class="text-left">
                          <div
                            class="text-uppercase font-weight-bold mb-1"
                            style="color: #777777; letter-spacing: 0.1em; font-size: 10px; letter-spacing: 0.91px"
                          >
                            AVAILABLE
                          </div>
                          <span
                            class="font-weight-bold"
                            :style="
                              $vuetify.display.xs
                                ? 'font-size: 13px; letter-spacing: 0.5px;'
                                : 'font-size: 14px; letter-spacing: 1px;'
                            "
                          >
                            {{ course.availableSessions || '—' }}
                          </span>
                        </v-col>
                      </v-row>
                    </v-col>
                  </v-row>
                </v-col>

                <!-- Right column: Buttons -->
                <CourseCardButtons
                  :course="course"
                  :show-details-button="showDetailsButton"
                  :display-info="displayInfo"
                  :is-selected="isSelected"
                  :is-button-disabled="isButtonDisabled"
                  :button-text="buttonText"
                  :schedule-dropdown-open="scheduleDropdownOpen"
                  :is-small-screen="isSmallScreen"
                  @view-details="handleViewDetails"
                  @remove-course="handleRemoveCourse"
                  @open-link="handleOpenLink"
                  @offering-selection="handleOfferingSelection"
                  @button-action="handleButtonAction"
                  @update:schedule-dropdown-open="scheduleDropdownOpen = $event"
                />
              </v-row>
            </div>
          </div>
        </v-row>

        <!-- Credit Designation (shown for history and past variants) -->
        <v-row
          v-if="(variant === 'history' || variant === 'past') && course.creditDesignationsDisplay"
          no-gutters
          class="mt-3"
        >
          <v-col cols="12">
            <div style="color: #747474; font-size: 14px">
              <span>Credit Designation: </span>
              <span v-html="course.creditDesignationsDisplay" />
            </div>
          </v-col>
        </v-row>

        <!-- Partnership Warning Banner (rendered separately from icon) -->
        <PartnershipWarning v-model="partnershipWarningExpanded" :warning="warningProp" :show-icon="false" />
      </v-col>

      <!-- Course Details Expansion Panel -->
      <CourseDetailsPanel :course="course" :is-open="displayInfo" @close="handleViewDetails" />
    </div>
  </div>
</template>

<script>
import { defineComponent } from 'vue';
import { Button as UiButton, Chip as UiChip } from '../ui/index.js';
import ScheduleDropdown from './ScheduleDropdown.vue';
import CourseDetailsPanel from './CourseDetailsPanel.vue';
import PartnershipWarning from './PartnershipWarning.vue';
import InfoTooltip from '../ui/InfoTooltip.vue';
import CourseCardButtons from './CourseCardButtons.vue';

export default defineComponent({
  name: 'CourseCard',

  components: {
    UiButton,
    UiChip,
    ScheduleDropdown,
    CourseDetailsPanel,
    PartnershipWarning,
    InfoTooltip,
    CourseCardButtons
  },

  props: {
    /**
     * Course object from the V2 API
     */
    course: {
      type: Object,
      required: true
    },

    /**
     * Whether this is the last course in the list (affects margin)
     */
    isLastCourse: {
      type: Boolean,
      default: false
    },

    /**
     * Whether the course is currently selected for enrollment
     */
    isSelected: {
      type: Boolean,
      default: false
    },

    /**
     * Whether to show the pathway badge
     */
    showPathwayBadge: {
      type: Boolean,
      default: false
    },

    /**
     * Whether to show the "View details" button
     */
    showDetailsButton: {
      type: Boolean,
      default: true
    },

    /**
     * Whether the dropdown is currently open (controlled by parent)
     */
    dropdownOpen: {
      type: Boolean,
      default: false
    },

    /**
     * Display variant: 'schedule', 'history', or 'past'
     * - schedule: Shows START/END/CREDITS (for Add Courses page)
     * - history: Shows TRANSCRIPT BY/CREDITS/GRADE (for Course History - Add to Transcript section)
     * - past: Shows START/END/CREDITS (for Course History - Past Courses section)
     */
    variant: {
      type: String,
      default: 'schedule',
      validator: (value) => ['schedule', 'history', 'past'].includes(value)
    }
  },

  emits: ['view-details', 'add-course', 'remove-course', 'dropdown-toggle', 'button-action'],

  data() {
    return {
      displayInfo: false,
      partnershipWarningExpanded: true,
      screenWidth: typeof window !== 'undefined' ? window.innerWidth : 1280
    };
  },

  computed: {
    /**
     * Check if screen is below 1280px
     */
    isSmallScreen() {
      return this.screenWidth < 1280;
    },
    /**
     * Format credit designations for display
     */
    formattedDesignations() {
      if (!this.course.academicInfo?.creditDesignations) {
        return [];
      }
      return this.course.academicInfo.creditDesignations;
    },

    /**
     * Computed property for dropdown state that syncs with parent
     */
    scheduleDropdownOpen: {
      get() {
        return this.dropdownOpen;
      },
      set(value) {
        this.$emit('dropdown-toggle', value);
      }
    },

    /**
     * Check if the course is scheduled
     */
    isScheduled() {
      return this.course.ui?.buttonLabel === 'Scheduled' || this.course.ui?.buttonLabel === 'Enrolled';
    },

    /**
     * Check if this is a transcripted course in AddCoursesV2 context
     */
    isTranscriptedCourse() {
      return (
        this.variant === 'schedule' &&
        this.course.ui?.buttonLabel === 'View Transcript' &&
        this.course.section?.name === 'Transcripted Courses'
      );
    },

    /**
     * Get the appropriate button text
     */
    buttonText() {
      if (this.isTranscriptedCourse) {
        return 'Transcripted';
      }
      return this.course.ui?.buttonLabel;
    },

    /**
     * Check if the button should be disabled
     */
    isButtonDisabled() {
      return this.course.ui?.buttonDisabled || this.isTranscriptedCourse;
    },

    /**
     * ui.detailsLayout > section.overrides.ui.detailsLayout > null (defaults to start-end-credits)
     */
    detailsLayout() {
      return this.course.ui?.detailsLayout || this.course.section?.overrides?.ui?.detailsLayout || null;
    },

    /**
     * Warning prop - hide partnership warning for scheduled courses and EnrollmentTypeLearnerPay enrollment type
     */
    warningProp() {
      if (this.isScheduled) return null;

      return this.course.warning;
    }
  },

  mounted() {
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', this.updateScreenWidth);
    }
  },

  beforeUnmount() {
    if (typeof window !== 'undefined') {
      window.removeEventListener('resize', this.updateScreenWidth);
    }
  },

  methods: {
    /**
     * Update screen width on window resize
     */
    updateScreenWidth() {
      if (typeof window !== 'undefined') {
        this.screenWidth = window.innerWidth;
      }
    },

    /**
     * Handle view details click - toggles inline expansion
     */
    handleViewDetails() {
      this.displayInfo = !this.displayInfo;
      this.$emit('view-details', this.course);
    },

    /**
     * Handle add course click
     */
    handleAddCourse() {
      this.$emit('add-course', {
        courseId: this.course.courseId,
        coursePrefix: this.course.coursePrefix,
        courseOfferingId: this.course.offering?.courseOfferingId,
        courseStartDate: this.course.offering?.startDate,
        course: this.course // Pass the full course object to preserve enrollment type
      });
    },

    /**
     * Handle remove course click (when already selected)
     */
    handleRemoveCourse() {
      this.$emit('remove-course', {
        courseId: this.course.courseId,
        coursePrefix: this.course.coursePrefix,
        courseOfferingId: this.course.offering?.courseOfferingId,
        courseStartDate: this.course.offering?.startDate
      });
    },

    /**
     * Handle offering selection from dropdown
     */
    handleOfferingSelection(option) {
      // Emit the same format as handleAddCourse
      this.$emit('add-course', {
        courseId: this.course.courseId,
        coursePrefix: this.course.coursePrefix,
        courseOfferingId: option.offering.offeringId,
        courseStartDate: option.offering.startDate,
        course: this.course // Pass the full course object to preserve enrollment type
      });
    },

    /**
     * Toggle the partnership warning expansion
     */
    togglePartnershipWarning() {
      this.partnershipWarningExpanded = !this.partnershipWarningExpanded;
    },

    /**
     * Handle button action click (for actions like "Add to Transcript")
     */
    handleButtonAction() {
      this.$emit('button-action', {
        courseId: this.course.courseId,
        coursePrefix: this.course.coursePrefix,
        action: this.course.ui?.buttonAction,
        enrollmentId: this.course.enrollment?.enrollmentId,
        courseOfferingId: this.course.offering?.courseOfferingId
      });
    },

    /**
     * Handle opening external link in new tab
     */
    handleOpenLink() {
      const linkUrl = this.course.section?.overrides?.ui?.linkUrl;
      if (linkUrl) {
        window.open(linkUrl, '_blank', 'noopener,noreferrer');
      }
    }
  }
});
</script>
